import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { renderKitPreview, getKitStyleById } from '../utils/kitUtils';
import { formatDateOrdinal } from '../utils/dateUtils';
import apiClient from '../utils/api';

interface GlobalRank {
  rank: number | null;
  hasRank: boolean;
  isUnranked: boolean;
}

const TeamWidget: React.FC = () => {
  const { user } = useAuth();
  const [globalRank, setGlobalRank] = useState<GlobalRank | null>(null);
  const [rankLoading, setRankLoading] = useState(false);

  useEffect(() => {
    const fetchGlobalRank = async () => {
      if (!user?.id) return;
      
      // Check if we have cached data with expiration
      const cacheKey = `global_rank_${user.id}`;
      const cacheTimestampKey = `global_rank_timestamp_${user.id}`;
      
      const cachedData = sessionStorage.getItem(cacheKey);
      const cacheTimestamp = sessionStorage.getItem(cacheTimestampKey);
      
      if (cachedData && cacheTimestamp) {
        try {
          const timestamp = parseInt(cacheTimestamp);
          const now = Date.now();
          const oneHour = 15 * 60 * 1000; // 1 hour in milliseconds
          
          // Check if cache is still valid (less than 1 hour old)
          if (now - timestamp < oneHour) {
            const parsed = JSON.parse(cachedData);
            setGlobalRank(parsed);
            setRankLoading(false);
            return;
          } else {
            // Cache expired, remove it
            sessionStorage.removeItem(cacheKey);
            sessionStorage.removeItem(cacheTimestampKey);
          }
        } catch (e) {
          // If parsing fails, remove invalid cache
          sessionStorage.removeItem(cacheKey);
          sessionStorage.removeItem(cacheTimestampKey);
        }
      }
      
      try {
        setRankLoading(true);
        const response = await apiClient.get(`/users/${user.id}/global-rank`);
        setGlobalRank(response.data);
        
        // Cache the data with timestamp
        sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
        sessionStorage.setItem(cacheTimestampKey, Date.now().toString());
      } catch (error) {
        console.error('Failed to fetch global rank:', error);
        setGlobalRank(null);
      } finally {
        setRankLoading(false);
      }
    };

    fetchGlobalRank();
  }, [user?.id]);

  if (!user) {
    return null;
  }



  return (
    <div className="card shadow rounded-lg bg-gray-50 dark:bg-gray-800/50">
      <div className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          {/* Main Content - Team Kit and Info */}
          <div className="flex items-center space-x-4 flex-1">
            {/* Team Kit or Create Kit Button */}
          {user.kitStyle ? (
            <div className="w-24 h-24 rounded-lg flex items-center justify-center">
              {(() => {
                const kitStyle = getKitStyleById(user.kitStyle);
                if (kitStyle) {
                  return renderKitPreview(
                    kitStyle,
                    user.kitPrimaryColour || '#2563eb',
                    user.kitSecondaryColour || '#ffffff',
                    'large'
                  );
                }
                return null;
              })()}
            </div>
          ) : (
            <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
              <Link
                to="/kit-designer"
                className="flex flex-col items-center justify-center w-full h-full text-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors rounded-lg"
              >
                <svg className="w-6 h-6 mb-1 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Create Kit</span>
              </Link>
            </div>
          )}
          
          {/* Team Info */}
          <div className="pl-4 flex-1">
            <div className="flex items-center gap-3 mb-1">
              <Link 
                to={`/users/${user.id}`}
                className="hover:underline"
              >
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {user.teamName}
                </h3>
              </Link>
         
            </div>
            
            {/* Global Rank */}
            <div className="flex items-center gap-2 mt-2">

              <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M8 21l8 0" />
                <path d="M12 17l0 4" />
                <path d="M7 4l10 0" />
                <path d="M17 4v8a5 5 0 0 1 -10 0v-8" />
                <path d="M3 9a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                <path d="M17 9a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
              </svg>

              <Link to={`/users/${user.id}`} style={{ color: 'var(--highlight)' }} className="flex-shrink-0 text-sm">
              Team Stats &amp; Trophies 
              </Link>
            </div>
            
            {/* Playing Since */}
            <div className="flex items-center gap-2 mt-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Est. {formatDateOrdinal(user.createdAt)}
              </span>
            </div>
            
            
              
              {!user.kitStyle && (
                <div className="flex flex-col gap-1 mt-2">
                <p className="text-xs" style={{ color: 'var(--highlight)' }}>
                  <Link to="/kit-designer" className="hover:underline">
                    Design your team kit →
                  </Link>
                </p>
                </div>
              )}
            

          </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col lg:flex-row gap-3 lg:gap-6 max-sm:mt-4">
            <Link
              to="/my-predictions"
              className="btn-primary font-medium px-5 py-3 md:py-2 rounded-md flex items-center justify-center gap-2 min-w-[140px]"
            >
              {/* <svg width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor" ><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" /><path d="M3 9h3v6h-3z" /><path d="M18 9h3v6h-3z" /><path d="M3 5m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z" /><path d="M12 5l0 14" /></svg> */}
              <svg width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /><path d="M12 7l4.76 3.45l-1.76 5.55h-6l-1.76 -5.55l4.76 -3.45" /><path d="M12 7v-4m3 13l2.5 3m-.74 -8.55l3.74 -1.45m-11.44 7.05l-2.56 2.95m.74 -8.55l-3.74 -1.45" /></svg>

              My Predictions
            </Link>
            
            <Link
              to="/leagues"
              className="btn-secondary font-medium border-white dark:border-black px-5 py-3 md:py-2 rounded-md flex items-center justify-center gap-2 min-w-[140px]"
              style={{ color: 'var(--highlight)' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10" />
                <path d="M12 5v2" />
                <path d="M12 10v1" />
                <path d="M12 14v1" />
                <path d="M12 18v1" />
                <path d="M7 3v2" />
                <path d="M17 3v2" />
                <path d="M15 10.5v3a1.5 1.5 0 0 0 3 0v-3a1.5 1.5 0 0 0 -3 0" />
                <path d="M6 9h1.5a1.5 1.5 0 0 1 0 3h-.5h.5a1.5 1.5 0 0 1 0 3h-1.5" />
              </svg>
              View Friends Leagues
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamWidget; 